type SnakeCase<S extends string> =
  S extends `${infer First}${infer Rest}`
    ? `${Lowercase<First>}${SnakeCaseHelper<Rest>}`
    : S;

type SnakeCaseHelper<S extends string> =
  S extends ''
    ? ''
    : S extends `-${infer Tail}`
      ? `_${SnakeCase<Tail>}`
      : S extends `${infer First}${infer Rest}`
        ? First extends Lowercase<First>
          ? `${First}${SnakeCaseHelper<Rest>}`
          : `_${Lowercase<First>}${SnakeCaseHelper<Rest>}`
        : S;

type CamelCase<S extends string> =
  S extends `${infer Head}_${infer Tail}`
    ? `${Head}${Capitalize<CamelCase<Tail>>}`
    : S extends `${infer Head}-${infer Tail}`
      ? `${Head}${Capitalize<CamelCase<Tail>>}`
      : S extends `${infer First}${infer Rest}`
        ? `${Lowercase<First>}${Rest}`
        : S;

type KeysToCamelCase<T> = T extends (infer U)[]
  ? KeysToCamelCase<U>[]
  : T extends object
    ? {
      [K in keyof T as CamelCase<Extract<K, string>>]: KeysToCamelCase<T[K]>
    }
    : T;

export const camelcase = <TString extends string>(str: TString) =>
  str.charAt(0).toLowerCase() + str.slice(1).replace(/_([a-z])|-([a-z])/g,
    g => g[1]?.toUpperCase() ?? '') as CamelCase<TString>;
export const snakecase = <TString extends string>(str: TString) =>
  str.replace(/([a-zA-Z])(?=[A-Z])|-/g,
    '$1_').toLowerCase() as SnakeCase<TString>;

const convertObject = <TObject extends object>(obj: TObject, conversionFunc: (string: string) => string) => {
  const newObj = {} as TObject;
  if (obj) {
    const keys = Object.keys(obj) as (keyof TObject)[];
    keys.forEach(k => {
      const newKey = conversionFunc(k as string) as keyof TObject;
      newObj[newKey] = obj[k];

      if (Array.isArray(newObj[newKey])) {
        newObj[newKey] = newObj[newKey].map((item: unknown) => {
          if (typeof item === 'object') {
            return convertObject(item as Record<string, unknown>, conversionFunc);
          }

          return item;
        }) as TObject[keyof TObject];
      } else if (newObj[newKey] && typeof newObj[newKey] === 'object') {
        newObj[newKey] = convertObject(newObj[newKey], conversionFunc);
      }
    });
  }

  return newObj;
};

export const convertObjToCamel = <TObject extends object>(
  obj: TObject,
): KeysToCamelCase<TObject> => convertObject(obj, camelcase) as KeysToCamelCase<TObject>;
